import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { GPA_LOGO } from "@/lib/assets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Loader2, MailCheck, ShieldCheck } from "lucide-react";
import { CAMPUSES } from "@/lib/institution";
import { ProgramCombobox } from "@/components/ProgramCombobox";

const TYPE_LABELS = {
  student: "Student", faculty: "Faculty", advisor: "Academic Advisor",
  employer: "Employer", prospect: "Interested in learning more",
};
// Access types offered on the public form (privileged roles are never offered).
const FORM_TYPES = ["student", "faculty", "advisor", "employer"];

export default function SignUp() {
  const [types, setTypes] = useState(FORM_TYPES);
  const [instructions, setInstructions] = useState("");
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", requested_role: "student",
    institution: "", id_number: "", reason: "", job_title: "", department: "",
    campus: "", program: "", phone: "", sponsor_name: "", sponsor_email: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null); // {kind: 'request'|'inquiry', message}

  useEffect(() => {
    api.get("/registration/config").then(({ data }) => {
      const allowed = (data.allowed_requested_user_types || []).filter((t) => FORM_TYPES.includes(t));
      if (allowed.length) setTypes(allowed);
      setInstructions(data.registration_instructions || "");
    }).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));
  const role = form.requested_role;
  const isProspect = role === "prospect";
  const isStaff = role === "faculty" || role === "advisor";
  const isEmployer = role === "employer";
  const isStudent = role === "student";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isProspect) {
        const { data } = await api.post("/registration/inquiry", {
          first_name: form.first_name, last_name: form.last_name || "-", email: form.email,
          organization: form.institution || undefined, interest: form.reason || undefined,
        });
        setDone({ kind: "inquiry", message: data.message });
      } else {
        await api.post("/registration/request", {
          first_name: form.first_name, last_name: form.last_name, email: form.email,
          requested_role: role, institution: form.institution || undefined,
          id_number: form.id_number || undefined, reason: form.reason || undefined,
          job_title: form.job_title || undefined, department: form.department || undefined,
          campus: form.campus || undefined, program: form.program || undefined,
          phone: form.phone || undefined,
          sponsor_name: form.sponsor_name || undefined, sponsor_email: form.sponsor_email || undefined,
          ref: localStorage.getItem("gpaplus_ref") || undefined,
        });
        setDone({ kind: "request" });
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="brand-hero relative hidden w-1/2 flex-col justify-between p-12 text-white lg:flex">
        <div className="brand-mesh absolute inset-0" />
        <Link to="/" className="relative inline-flex items-center rounded-lg bg-white px-3 py-2">
          <img src={GPA_LOGO} alt="GPA+" className="h-9 w-auto" />
        </Link>
        <div className="relative">
          <h2 className="font-heading text-4xl font-extrabold leading-tight">Request GPA+ access</h2>
          <p className="mt-4 max-w-md text-slate-300">
            GPA+ is a controlled environment. Tell us your relationship to GPA+ and an administrator will
            review your request before you sign in.
          </p>
          <div className="mt-6 flex items-center gap-2 text-sm text-slate-300">
            <ShieldCheck className="h-5 w-5 text-[#78be21]" /> Reviewed & approved by an administrator
          </div>
        </div>
        <p className="relative text-xs text-slate-400">FERPA-aligned · Role-based access · Beyond Grades. Beyond Limits. Beyond Ready.</p>
      </div>

      <div className="flex w-full items-center justify-center bg-slate-50 px-6 py-12 lg:w-1/2">
        <div className="w-full max-w-md">
          <Link to="/login" className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#003057]">
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>

          {done ? (
            <div data-testid="request-access-success" className="rounded-2xl border border-[#78be21]/40 bg-white p-8 shadow-sm">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#78be21]/15">
                <MailCheck className="h-7 w-7 text-[#5fa114]" />
              </div>
              {done.kind === "inquiry" ? (
                <>
                  <h1 className="font-heading text-2xl font-extrabold text-[#003057]">Thanks for your interest</h1>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{done.message}</p>
                </>
              ) : (
                <>
                  <h1 className="font-heading text-2xl font-extrabold text-[#003057]">Request received</h1>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                    Your GPA+ access request has been received. Please check your inbox to verify your email.
                    An administrator must approve your account before you can access GPA+ — you'll receive an
                    email when your request has been reviewed.
                  </p>
                </>
              )}
              <Link to="/login">
                <Button className="mt-6 w-full rounded-full bg-[#003057] py-6 text-base font-bold hover:bg-[#004275]" data-testid="success-to-login">
                  Return to sign in
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-3xl font-extrabold text-[#003057]">Request access</h1>
              <p className="mt-1 text-slate-500">Tell us your relationship to GPA+.</p>
              {instructions && <p className="mt-3 rounded-lg bg-[#003057]/5 px-3 py-2 text-sm text-[#003057]">{instructions}</p>}

              <form onSubmit={submit} className="mt-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="first_name">First name</Label>
                    <Input id="first_name" data-testid="req-first-name" value={form.first_name} onChange={set("first_name")} className="mt-1.5" required />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last name{isProspect && <span className="text-slate-400"> (optional)</span>}</Label>
                    <Input id="last_name" data-testid="req-last-name" value={form.last_name} onChange={set("last_name")} className="mt-1.5" required={!isProspect} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email address</Label>
                  <Input id="email" data-testid="req-email" type="email" value={form.email} onChange={set("email")} className="mt-1.5" required />
                </div>
                <div>
                  <Label>Requested relationship / access type</Label>
                  <Select value={role} onValueChange={(v) => setForm((f) => ({ ...f, requested_role: v }))}>
                    <SelectTrigger data-testid="req-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {types.map((t) => (<SelectItem key={t} value={t} data-testid={`req-type-${t}`}>{TYPE_LABELS[t] || t}</SelectItem>))}
                      <SelectItem value="prospect" data-testid="req-type-prospect">{TYPE_LABELS.prospect}</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-[11px] text-slate-400">Administrator roles are assigned separately by authorized staff.</p>
                </div>

                {/* Prospect / lead capture — no account is created */}
                {isProspect && (
                  <>
                    <div>
                      <Label htmlFor="org">Organization <span className="text-slate-400">(optional)</span></Label>
                      <Input id="org" data-testid="req-institution" value={form.institution} onChange={set("institution")} className="mt-1.5" placeholder="Your school or company" />
                    </div>
                    <div>
                      <Label htmlFor="interest">What would you like to learn about GPA+?</Label>
                      <Textarea id="interest" data-testid="req-reason" value={form.reason} onChange={set("reason")} rows={3} className="mt-1.5" placeholder="Tell us what you're hoping to explore." />
                    </div>
                  </>
                )}

                {/* Full request — conditional by access type */}
                {!isProspect && (
                  <>
                    <div>
                      <Label htmlFor="institution">{isEmployer ? "Organization" : "Institution / College"}{isEmployer ? "" : <span className="text-slate-400"> (optional)</span>}</Label>
                      <Input id="institution" data-testid="req-institution" value={form.institution} onChange={set("institution")} placeholder={isEmployer ? "Company name" : "e.g. Lone Star College"} className="mt-1.5" required={isEmployer} />
                    </div>

                    {isStudent && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="campus">Campus <span className="text-slate-400">(opt)</span></Label>
                          <Select value={form.campus || ""} onValueChange={(v) => setForm((f) => ({ ...f, campus: v === "__none" ? "" : v }))}>
                            <SelectTrigger id="campus" data-testid="req-campus" className="mt-1.5"><SelectValue placeholder="Select campus" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none">Not specified</SelectItem>
                              {CAMPUSES.map((c) => <SelectItem key={c} value={c}>LSC-{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="program">Program / Major <span className="text-slate-400">(opt)</span></Label>
                          <ProgramCombobox value={form.program || ""} onChange={(v) => setForm((f) => ({ ...f, program: v }))} testid="req-program" />
                        </div>
                      </div>
                    )}

                    {isStaff && (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="job_title">Job title</Label>
                            <Input id="job_title" data-testid="req-job-title" value={form.job_title} onChange={set("job_title")} className="mt-1.5" />
                          </div>
                          <div>
                            <Label htmlFor="campus">Campus <span className="text-slate-400">(opt)</span></Label>
                            <Select value={form.campus || ""} onValueChange={(v) => setForm((f) => ({ ...f, campus: v === "__none" ? "" : v }))}>
                            <SelectTrigger id="campus" data-testid="req-campus" className="mt-1.5"><SelectValue placeholder="Select campus" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none">Not specified</SelectItem>
                              {CAMPUSES.map((c) => <SelectItem key={c} value={c}>LSC-{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="department">Department / Program</Label>
                          <Input id="department" data-testid="req-department" value={form.department} onChange={set("department")} className="mt-1.5" />
                        </div>
                      </>
                    )}

                    {isEmployer && (
                      <div>
                        <Label htmlFor="job_title">Job title</Label>
                        <Input id="job_title" data-testid="req-job-title" value={form.job_title} onChange={set("job_title")} className="mt-1.5" required />
                      </div>
                    )}

                    {(isStudent || isStaff) && (
                      <div>
                        <Label htmlFor="id_number">{isStudent ? "Student ID" : "Employee ID"} <span className="text-slate-400">(if applicable)</span></Label>
                        <Input id="id_number" data-testid="req-id-number" value={form.id_number} onChange={set("id_number")} placeholder="issued by your institution" className="mt-1.5" />
                      </div>
                    )}

                    <div>
                      <Label htmlFor="phone">Phone <span className="text-slate-400">(optional)</span></Label>
                      <Input id="phone" data-testid="req-phone" value={form.phone} onChange={set("phone")} placeholder="For follow-up if needed" className="mt-1.5" />
                    </div>

                    {isEmployer && (
                      <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">LSC sponsor / contact <span className="normal-case text-slate-400">(optional — if you have one)</span></p>
                        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div>
                            <Label htmlFor="sponsor_name">Sponsor / contact name</Label>
                            <Input id="sponsor_name" data-testid="req-sponsor-name" value={form.sponsor_name} onChange={set("sponsor_name")} className="mt-1.5" />
                          </div>
                          <div>
                            <Label htmlFor="sponsor_email">Sponsor / contact email</Label>
                            <Input id="sponsor_email" type="email" data-testid="req-sponsor-email" value={form.sponsor_email} onChange={set("sponsor_email")} className="mt-1.5" />
                          </div>
                        </div>
                      </div>
                    )}

                    <div>
                      <Label htmlFor="reason">{isEmployer ? "How do you intend to use GPA+?" : "How will you use GPA+?"}</Label>
                      <Textarea id="reason" data-testid="req-reason" value={form.reason} onChange={set("reason")} rows={3} className="mt-1.5" placeholder="A sentence or two about your role and why you need access." />
                    </div>
                  </>
                )}

                {error && <p data-testid="req-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{error}</p>}
                <Button type="submit" data-testid="req-submit" disabled={loading}
                  className="w-full rounded-full bg-[#003057] py-6 text-base font-bold hover:bg-[#004275]">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (isProspect ? "Send my inquiry" : "Request access")}
                </Button>
              </form>

              <p className="mt-4 text-center text-sm text-slate-500">
                Already approved?{" "}
                <Link to="/login" className="font-semibold text-[#005291] hover:text-[#78be21]">Sign in</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
